import { useEffect, useRef, useState } from "react";
import "./styles.css";

type Screen = "start" | "game" | "gameover";

type Point = {
  x: number;
  y: number;
};

type Target = {
  id: number;
  x: number;
  y: number;
  bornAt: number;
  smashed: boolean;
  vx: number;
  vy: number;
};

type HitResult = {
  hit: boolean;
  points: number;
  impactX: number;
  impactY: number;
  targetId?: number;
};

type Shot = {
  id: number;
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  launched: boolean;
  duration: number;
};

export default function App() {
  const GAME_TIME = 30;

  const arenaRef = useRef<HTMLDivElement | null>(null);

  const swipeStartRef = useRef<Point | null>(null);
  const swipeCurrentRef = useRef<Point | null>(null);

  const isSwipingRef = useRef(false);
  const shotLockedRef = useRef(false);

  const [screen, setScreen] = useState<Screen>("start");

  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(GAME_TIME);

  const [targets, setTargets] = useState<Target[]>([]);

  const [shot, setShot] = useState<Shot | null>(null);

  const [swipeCurrent, setSwipeCurrent] = useState<Point | null>(null);

  const [hitText, setHitText] = useState("");

  const [shotsThrown, setShotsThrown] = useState(0);
  const [targetsHit, setTargetsHit] = useState(0);
  const [bullseyes, setBullseyes] = useState(0);

  const [combo, setCombo] = useState(0);
  const [maxCombo, setMaxCombo] = useState(0);

  /* =========================
     DIFFICULTY
  ========================= */

  function getDifficulty(currentScore: number) {
    if (currentScore >= 2000) {
      return {
        targetCount: 3,
        lifetime: 1300,
        moving: true,
        speed: 0.135,
      };
    }

    if (currentScore >= 1500) {
      return {
        targetCount: 3,
        lifetime: 1500,
        moving: true,
        speed: 0.11,
      };
    }

    if (currentScore >= 1000) {
      return {
        targetCount: 2,
        lifetime: 1600,
        moving: true,
        speed: 0.08,
      };
    }

    if (currentScore >= 500) {
      return {
        targetCount: 2,
        lifetime: 2000,
        moving: false,
        speed: 0,
      };
    }

    return {
      targetCount: 1,
      lifetime: 2400,
      moving: false,
      speed: 0,
    };
  }

  /* =========================
     TIMER
  ========================= */

  useEffect(() => {
    if (screen !== "game") return;

    if (timeLeft <= 0) {
      setScreen("gameover");
      setShot(null);
      return;
    }

    const timer = window.setTimeout(() => {
      setTimeLeft((old) => old - 1);
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [screen, timeLeft]);

  /* =========================
     START
  ========================= */

  function startGame() {
    setScore(0);
    setTimeLeft(GAME_TIME);

    setShotsThrown(0);
    setTargetsHit(0);
    setBullseyes(0);

    setCombo(0);
    setMaxCombo(0);

    setShot(null);
    setHitText("");
    setSwipeCurrent(null);

    swipeStartRef.current = null;
    swipeCurrentRef.current = null;

    isSwipingRef.current = false;
    shotLockedRef.current = false;

    setTargets([
      createTarget(false, 0),
    ]);

    setScreen("game");
  }

  /* =========================
     TARGET CREATION
  ========================= */

  function createTarget(
    moving: boolean,
    speed: number
  ): Target {
    const angle =
      Math.random() * Math.PI * 2;

    return {
      id:
        Date.now() +
        Math.floor(Math.random() * 100000),

      x: 18 + Math.random() * 64,
      y: 18 + Math.random() * 36,

      bornAt: Date.now(),

      smashed: false,

      vx: moving
        ? Math.cos(angle) * speed
        : 0,

      vy: moving
        ? Math.sin(angle) * speed
        : 0,
    };
  }

  /* =========================
     KEEP CORRECT TARGET COUNT
  ========================= */

  useEffect(() => {
    if (screen !== "game") return;

    const difficulty = getDifficulty(score);

    setTargets((oldTargets) => {
      let nextTargets = [...oldTargets];

      const alive =
        nextTargets.filter(
          (target) => !target.smashed
        );

      while (
        alive.length <
        difficulty.targetCount
      ) {
        const newTarget =
          createTarget(
            difficulty.moving,
            difficulty.speed
          );

        nextTargets.push(newTarget);
        alive.push(newTarget);
      }

      return nextTargets;
    });
  }, [score, screen]);

  /* =========================
     TARGET EXPIRY
  ========================= */

  useEffect(() => {
    if (screen !== "game") return;

    const interval =
      window.setInterval(() => {
        const difficulty =
          getDifficulty(score);

        const now = Date.now();

        setTargets((oldTargets) => {
          let nextTargets =
            oldTargets.filter(
              (target) =>
                target.smashed ||
                now - target.bornAt <
                  difficulty.lifetime
            );

          const aliveCount =
            nextTargets.filter(
              (target) =>
                !target.smashed
            ).length;

          const missing =
            difficulty.targetCount -
            aliveCount;

          for (
            let i = 0;
            i < missing;
            i++
          ) {
            nextTargets.push(
              createTarget(
                difficulty.moving,
                difficulty.speed
              )
            );
          }

          return nextTargets;
        });
      }, 80);

    return () =>
      window.clearInterval(interval);
  }, [score, screen]);

  /* =========================
     TARGET MOVEMENT
  ========================= */

  useEffect(() => {
    if (screen !== "game") return;

    const difficulty =
      getDifficulty(score);

    if (!difficulty.moving) return;

    let animationId: number;

    let lastTime = performance.now();

    function animate(now: number) {
      const delta = now - lastTime;

      lastTime = now;

      setTargets((oldTargets) =>
        oldTargets.map((target) => {
          if (target.smashed) {
            return target;
          }

          let newX =
            target.x +
            target.vx * delta;

          let newY =
            target.y +
            target.vy * delta;

          let newVX = target.vx;
          let newVY = target.vy;

          if (newX < 14) {
            newX = 14;
            newVX =
              Math.abs(newVX);
          }

          if (newX > 86) {
            newX = 86;
            newVX =
              -Math.abs(newVX);
          }

          if (newY < 16) {
            newY = 16;
            newVY =
              Math.abs(newVY);
          }

          if (newY > 56) {
            newY = 56;
            newVY =
              -Math.abs(newVY);
          }

          return {
            ...target,
            x: newX,
            y: newY,
            vx: newVX,
            vy: newVY,
          };
        })
      );

      animationId =
        requestAnimationFrame(
          animate
        );
    }

    animationId =
      requestAnimationFrame(
        animate
      );

    return () =>
      cancelAnimationFrame(
        animationId
      );
  }, [score, screen]);

  /* =========================
     POINTER POSITION
  ========================= */

  function getArenaPoint(
    clientX: number,
    clientY: number
  ): Point | null {
    const arena =
      arenaRef.current;

    if (!arena) return null;

    const rect =
      arena.getBoundingClientRect();

    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
  }

  /* =========================
     SWIPE
  ========================= */

  function handlePointerDown(
    event: React.PointerEvent<HTMLDivElement>
  ) {
    if (screen !== "game") return;
    if (shotLockedRef.current) return;

    const point =
      getArenaPoint(
        event.clientX,
        event.clientY
      );

    if (!point) return;

    const arena =
      arenaRef.current;

    if (!arena) return;

    const rect =
      arena.getBoundingClientRect();

    if (
      point.y <
      rect.height * 0.48
    ) {
      return;
    }

    isSwipingRef.current = true;

    swipeStartRef.current =
      point;

    swipeCurrentRef.current =
      point;

    setSwipeCurrent(point);

    try {
      event.currentTarget.setPointerCapture(
        event.pointerId
      );
    } catch {
      //
    }
  }

  function handlePointerMove(
    event: React.PointerEvent<HTMLDivElement>
  ) {
    if (
      !isSwipingRef.current
    ) {
      return;
    }

    const point =
      getArenaPoint(
        event.clientX,
        event.clientY
      );

    if (!point) return;

    swipeCurrentRef.current =
      point;

    setSwipeCurrent(point);
  }

  function handlePointerUp(
    event: React.PointerEvent<HTMLDivElement>
  ) {
    if (
      !isSwipingRef.current
    ) {
      return;
    }

    const start =
      swipeStartRef.current;

    const end =
      getArenaPoint(
        event.clientX,
        event.clientY
      ) ??
      swipeCurrentRef.current;

    isSwipingRef.current =
      false;

    swipeStartRef.current =
      null;

    swipeCurrentRef.current =
      null;

    setSwipeCurrent(null);

    if (!start || !end) return;

    const dx =
      end.x - start.x;

    const dy =
      end.y - start.y;

    const distance =
      Math.sqrt(
        dx * dx +
        dy * dy
      );

    if (distance < 25) {
      showText("SWIPE!");
      return;
    }

    if (dy > -10) {
      showText(
        "SWIPE UP!"
      );

      return;
    }

    throwShuriken(
      start,
      end
    );
  }

  function handlePointerCancel() {
    isSwipingRef.current =
      false;

    swipeStartRef.current =
      null;

    swipeCurrentRef.current =
      null;

    setSwipeCurrent(null);
  }

  /* =========================
     THROW
  ========================= */

  function throwShuriken(
    swipeStartPoint: Point,
    swipeEndPoint: Point
  ) {
    const arena =
      arenaRef.current;

    if (!arena) return;

    shotLockedRef.current =
      true;

    const arenaRect =
      arena.getBoundingClientRect();

    const dx =
      swipeEndPoint.x -
      swipeStartPoint.x;

    const dy =
      swipeEndPoint.y -
      swipeStartPoint.y;

    const swipeLength =
      Math.sqrt(
        dx * dx +
        dy * dy
      );

    if (!swipeLength) {
      shotLockedRef.current =
        false;

      return;
    }

    const directionX =
      dx / swipeLength;

    const directionY =
      dy / swipeLength;

    const startX =
      arenaRect.width * 0.59;

    const startY =
      arenaRect.height * 0.78;

    const fullDistance =
      Math.max(
        arenaRect.width,
        arenaRect.height
      ) * 1.15;

    const fullEndX =
      startX +
      directionX *
        fullDistance;

    const fullEndY =
      startY +
      directionY *
        fullDistance;

    let bestResult: HitResult = {
      hit: false,
      points: 0,
      impactX: fullEndX,
      impactY: fullEndY,
    };

    let nearestDistance =
      Infinity;

    /*
      Check every active target.

      Only the FIRST target along
      the trajectory gets hit.
    */

    const targetElements =
      Array.from(
        arena.querySelectorAll(
          ".target-wrapper"
        )
      ) as HTMLElement[];

    for (
      const targetElement of
      targetElements
    ) {
      const id =
        Number(
          targetElement.dataset
            .targetId
        );

      const targetRect =
        targetElement.getBoundingClientRect();

      const centreX =
        targetRect.left -
        arenaRect.left +
        targetRect.width / 2;

      const centreY =
        targetRect.top -
        arenaRect.top +
        targetRect.height / 2;

      const result =
        calculateHit(
          startX,
          startY,
          fullEndX,
          fullEndY,
          centreX,
          centreY,
          targetRect.width / 2
        );

      if (!result.hit) {
        continue;
      }

      const distanceFromStart =
        Math.sqrt(
          (
            result.impactX -
            startX
          ) ** 2 +
            (
              result.impactY -
              startY
            ) ** 2
        );

      if (
        distanceFromStart <
        nearestDistance
      ) {
        nearestDistance =
          distanceFromStart;

        bestResult = {
          ...result,
          targetId: id,
        };
      }
    }

    const endX =
      bestResult.hit
        ? bestResult.impactX
        : fullEndX;

    const endY =
      bestResult.hit
        ? bestResult.impactY
        : fullEndY;

    const actualDistance =
      Math.sqrt(
        (endX - startX) ** 2 +
          (endY - startY) ** 2
      );

    const duration =
      Math.max(
        220,
        Math.min(
          480,
          actualDistance * 0.78
        )
      );

    const shotId =
      Date.now();

    setShot({
      id: shotId,
      startX,
      startY,
      endX,
      endY,
      launched: false,
      duration,
    });

    setShotsThrown(
      (old) => old + 1
    );

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setShot(
          (current) => {
            if (
              !current ||
              current.id !==
                shotId
            ) {
              return current;
            }

            return {
              ...current,
              launched: true,
            };
          }
        );
      });
    });

    window.setTimeout(() => {
      if (
        bestResult.hit &&
        bestResult.targetId !==
          undefined
      ) {
        registerHit(
          bestResult.targetId,
          bestResult.points
        );
      } else {
        registerMiss();
      }
    }, duration);

    window.setTimeout(() => {
      setShot(null);

      shotLockedRef.current =
        false;
    }, duration + 150);
  }

  /* =========================
     COLLISION
  ========================= */

  function calculateHit(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    targetX: number,
    targetY: number,
    targetRadius: number
  ): HitResult {
    const lineX =
      x2 - x1;

    const lineY =
      y2 - y1;

    const lengthSquared =
      lineX * lineX +
      lineY * lineY;

    if (!lengthSquared) {
      return {
        hit: false,
        points: 0,
        impactX: x2,
        impactY: y2,
      };
    }

    let t =
      ((targetX - x1) *
        lineX +
        (targetY - y1) *
          lineY) /
      lengthSquared;

    t = Math.max(
      0,
      Math.min(1, t)
    );

    const closestX =
      x1 +
      t * lineX;

    const closestY =
      y1 +
      t * lineY;

    const distanceX =
      targetX -
      closestX;

    const distanceY =
      targetY -
      closestY;

    const distance =
      Math.sqrt(
        distanceX *
          distanceX +
          distanceY *
            distanceY
      );

    const ratio =
      distance /
      targetRadius;

    if (ratio > 1) {
      return {
        hit: false,
        points: 0,
        impactX: x2,
        impactY: y2,
      };
    }

    let points = 25;

    if (ratio <= 0.2) {
      points = 100;
    } else if (
      ratio <= 0.42
    ) {
      points = 75;
    } else if (
      ratio <= 0.68
    ) {
      points = 50;
    }

    return {
      hit: true,
      points,
      impactX: closestX,
      impactY: closestY,
    };
  }

  /* =========================
     HIT
  ========================= */

  function registerHit(
    targetId: number,
    points: number
  ) {
    setTargets((oldTargets) =>
      oldTargets.map(
        (target) =>
          target.id ===
          targetId
            ? {
                ...target,
                smashed: true,
              }
            : target
      )
    );

    setScore(
      (old) =>
        old + points
    );

    setTargetsHit(
      (old) => old + 1
    );

    if (points === 100) {
      setBullseyes(
        (old) =>
          old + 1
      );
    }

    setCombo(
      (oldCombo) => {
        const newCombo =
          oldCombo + 1;

        setMaxCombo(
          (oldMax) =>
            Math.max(
              oldMax,
              newCombo
            )
        );

        return newCombo;
      }
    );

    if (points === 100) {
      setHitText(
        "PERFECT! +100"
      );
    } else {
      setHitText(
        `+${points}`
      );
    }

    /*
      Remove smashed target then
      refill count automatically.
    */

    window.setTimeout(() => {
      setTargets(
        (oldTargets) =>
          oldTargets.filter(
            (target) =>
              target.id !==
              targetId
          )
      );

      setHitText("");
    }, 430);
  }

  function registerMiss() {
    setCombo(0);

    setHitText("MISS");

    window.setTimeout(() => {
      setHitText("");
    }, 320);
  }

  function showText(
    text: string
  ) {
    setHitText(text);

    window.setTimeout(() => {
      setHitText("");
    }, 350);
  }

  const accuracy =
    shotsThrown === 0
      ? 0
      : Math.round(
          (targetsHit /
            shotsThrown) *
            100
        );

  /* =========================
     JSX
  ========================= */

  return (
    <div className="app">

      <div className="game-wrapper">

        <div
          ref={arenaRef}
          className="game-arena"

          onPointerDown={
            handlePointerDown
          }

          onPointerMove={
            handlePointerMove
          }

          onPointerUp={
            handlePointerUp
          }

          onPointerCancel={
            handlePointerCancel
          }
        >

          <img
            src="/dojo-bg.png"
            className="dojo-background"
            alt=""
          />

          {screen === "start" && (
            <div className="menu-screen">

              <div className="title-panel">

                <div className="main-title">
                  NINJACAT
                </div>

                <div className="target-title">
                  TARGET
                </div>

                <div className="subtitle">
                  SHURIKEN CHALLENGE
                </div>

              </div>

              <div className="start-cat">
                <img
                  src="/ninjacat-back.png"
                  alt="NinjaCat"
                />
              </div>

              <button
                className="play-button"

                onPointerDown={
                  (event) =>
                    event.stopPropagation()
                }

                onClick={
                  startGame
                }
              >
                PLAY
              </button>

            </div>
          )}

          {screen === "game" && (
            <>

              <div className="hud">

                <div className="hud-box">

                  <span className="hud-label">
                    TIME
                  </span>

                  <span className="hud-value">
                    {timeLeft}
                  </span>

                </div>

                <div className="hud-box">

                  <span className="hud-label">
                    SCORE
                  </span>

                  <span className="hud-value">
                    {score}
                  </span>

                </div>

              </div>

              {combo >= 2 && (
                <div className="combo">
                  COMBO x{combo}
                </div>
              )}

              {targets.map(
                (target) => (

                  <div
                    key={target.id}

                    data-target-id={
                      target.id
                    }

                    className={
                      target.smashed
                        ? "target-wrapper smashing"
                        : "target-wrapper"
                    }

                    style={{
                      left:
                        `${target.x}%`,

                      top:
                        `${target.y}%`,
                    }}
                  >

                    {!target.smashed && (
                      <img
                        src="/target.png"
                        className="target-image"
                        alt="Target"
                      />
                    )}

                    {target.smashed && (
                      <div className="smash-pieces">

                        <img
                          src="/target.png"
                          className="piece piece-1"
                          alt=""
                        />

                        <img
                          src="/target.png"
                          className="piece piece-2"
                          alt=""
                        />

                        <img
                          src="/target.png"
                          className="piece piece-3"
                          alt=""
                        />

                        <img
                          src="/target.png"
                          className="piece piece-4"
                          alt=""
                        />

                        <img
                          src="/target.png"
                          className="piece piece-5"
                          alt=""
                        />

                        <div className="impact-flash" />

                      </div>
                    )}

                  </div>

                )
              )}

              {hitText && (
                <div
                  className={
                    hitText ===
                    "MISS"
                      ? "miss-text"
                      : hitText.includes(
                          "PERFECT"
                        )
                      ? "perfect-text"
                      : "shot-score"
                  }
                >
                  {hitText}
                </div>
              )}

              <div className="ninjacat-wrapper">

                <img
                  src="/ninjacat-back.png"
                  className="ninjacat"
                  alt="NinjaCat"
                />

              </div>

              {swipeCurrent && (
                <div
                  className="swipe-glow"

                  style={{
                    left:
                      swipeCurrent.x,

                    top:
                      swipeCurrent.y,
                  }}
                />
              )}

              {shot && (
                <div className="shot-layer">

                  <div
                    className={
                      shot.launched
                        ? "shuriken-holder launched"
                        : "shuriken-holder"
                    }

                    style={
                      {
                        left:
                          shot.launched
                            ? shot.endX
                            : shot.startX,

                        top:
                          shot.launched
                            ? shot.endY
                            : shot.startY,

                        transitionDuration:
                          `${shot.duration}ms`,
                      } as React.CSSProperties
                    }
                  >

                    <div className="shuriken-tail" />

                    <img
                      src="/shuriken1.png"
                      className="flying-shuriken"
                      alt=""
                    />

                  </div>

                </div>
              )}

            </>
          )}

          {screen === "gameover" && (
            <div className="gameover-screen">

              <div className="gameover-title">
                GAME OVER
              </div>

              <div className="final-score-label">
                FINAL SCORE
              </div>

              <div className="final-score">
                {score}
              </div>

              <div className="stats-panel">

                <div className="stat-row">
                  <span>
                    ACCURACY
                  </span>

                  <strong>
                    {accuracy}%
                  </strong>
                </div>

                <div className="stat-row">
                  <span>
                    TARGETS HIT
                  </span>

                  <strong>
                    {targetsHit}
                  </strong>
                </div>

                <div className="stat-row">
                  <span>
                    BULLSEYES
                  </span>

                  <strong>
                    {bullseyes}
                  </strong>
                </div>

                <div className="stat-row">
                  <span>
                    MAX COMBO
                  </span>

                  <strong>
                    x{maxCombo}
                  </strong>
                </div>

              </div>

              <button
                className="play-button"

                onPointerDown={
                  (event) =>
                    event.stopPropagation()
                }

                onClick={
                  startGame
                }
              >
                PLAY AGAIN
              </button>

              <button
                className="menu-button"

                onPointerDown={
                  (event) =>
                    event.stopPropagation()
                }

                onClick={() =>
                  setScreen(
                    "start"
                  )
                }
              >
                MAIN MENU
              </button>

            </div>
          )}

        </div>

      </div>

    </div>
  );
}